import { CITY_CENTER } from '../shared/constants';
import { TAU } from '../shared/math';
import type { ClassId, EnemyKind, Obstacle } from '../shared/types';
import { drawItemIcon } from './icons';
/**
 * All artwork is generated with canvas paths - the game ships no image assets.
 * Every draw function expects the origin to be the entity's feet (ground contact
 * point) and is drawn towards -y.
 */

export interface BodyOptions {
  /** 1 -> just started an attack, 0 -> animation finished. */
  swing: number;
  /** 0..1 smoothed walk amount. */
  moving: number;
  /** 0..1 white hit flash. */
  flash: number;
  /** 0..1 alpha multiplier. */
  alpha: number;
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
}

export function drawShadow(
  ctx: CanvasRenderingContext2D,
  radius: number,
  squash = 1,
  alpha = 0.32,
): void {
  ctx.save();
  ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
  ctx.beginPath();
  ctx.ellipse(0, 0, radius * 1.05, radius * 0.46 * squash, 0, 0, TAU);
  ctx.fill();
  ctx.restore();
}

/* ------------------------------------------------------------------------ terrain */

export function drawTree(ctx: CanvasRenderingContext2D, o: Obstacle, t: number): void {
  const r = o.r;
  const sway = Math.sin(t * 0.8 + o.x * 0.01) * r * 0.045;

  ctx.save();
  drawShadow(ctx, r * 1.1, 1, 0.3);

  ctx.fillStyle = '#4a3524';
  ctx.beginPath();
  ctx.moveTo(-r * 0.16, 0);
  ctx.lineTo(r * 0.16, 0);
  ctx.lineTo(r * 0.11 + sway * 0.4, -r * 0.85);
  ctx.lineTo(-r * 0.11 + sway * 0.4, -r * 0.85);
  ctx.closePath();
  ctx.fill();

  const clusters: Array<[number, number, number, string]> = [
    [-r * 0.52, -r * 1.28, r * 0.72, '#26492c'],
    [r * 0.5, -r * 1.22, r * 0.66, '#2a5330'],
    [0, -r * 1.72, r * 0.82, '#326038'],
    [-r * 0.2, -r * 1.34, r * 0.6, '#3a6d40'],
  ];
  for (const [cx, cy, cr, color] of clusters) {
    ctx.fillStyle = color;
    circle(ctx, cx + sway, cy, cr);
    ctx.fill();
  }

  ctx.fillStyle = 'rgba(150, 220, 150, 0.16)';
  circle(ctx, -r * 0.3 + sway, -r * 2.0, r * 0.44);
  ctx.fill();
  ctx.restore();
}

export function drawRock(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  const seed = o.x * 0.37 + o.y * 0.11;
  ctx.save();
  drawShadow(ctx, r * 1.02, 1, 0.34);

  ctx.beginPath();
  const points = 9;
  for (let i = 0; i <= points; i++) {
    const a = (i / points) * TAU;
    const wobble = 0.82 + Math.sin(seed + i * 2.1) * 0.16;
    const px = Math.cos(a) * r * wobble;
    const py = Math.sin(a) * r * wobble * 0.72 - r * 0.42;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = '#5d6470';
  ctx.fill();
  ctx.strokeStyle = '#3c424c';
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(-r * 0.2, -r * 0.72, r * 0.44, r * 0.28, -0.5, 0, TAU);
  ctx.fillStyle = 'rgba(190, 200, 214, 0.28)';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(r * 0.28, -r * 0.22, r * 0.16, r * 0.1, 0.4, 0, TAU);
  ctx.fillStyle = 'rgba(30, 34, 40, 0.35)';
  ctx.fill();
  ctx.restore();
}

/* ------------------------------------------------------------------------ players */

export function drawPlayerBody(
  ctx: CanvasRenderingContext2D,
  cls: ClassId,
  aim: number,
  t: number,
  radius: number,
  options: BodyOptions,
): void {
  const { swing, moving, flash, alpha } = options;
  const bob = Math.sin(t * 9) * moving * 2.2;
  const breathe = Math.sin(t * 2.2) * 0.4;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(0, bob);
  drawShadow(ctx, radius, 1, 0.34 * alpha);

  const bodyY = -radius * 1.15 + breathe;
  const bodyR = radius * 0.95;
  const look = Math.cos(aim) * 0.5;
  const lookY = Math.sin(aim) * 0.22;

  const colors = CLASS_PALETTE[cls];

  // --- legs
  ctx.fillStyle = colors.dark;
  const legSwing = Math.sin(t * 9) * moving * radius * 0.22;
  ctx.beginPath();
  ctx.roundRect(-radius * 0.52, -radius * 0.55, radius * 0.36, radius * 0.6 + legSwing, 3);
  ctx.roundRect(radius * 0.16, -radius * 0.55, radius * 0.36, radius * 0.6 - legSwing, 3);
  ctx.fill();

  // --- torso
  ctx.fillStyle = colors.main;
  ctx.strokeStyle = colors.dark;
  ctx.lineWidth = 2.4;
  circle(ctx, 0, bodyY, bodyR);
  ctx.fill();
  ctx.stroke();

  // --- shoulders / armour trim
  ctx.fillStyle = colors.light;
  circle(ctx, -bodyR * 0.86, bodyY + bodyR * 0.16, radius * 0.3);
  ctx.fill();
  circle(ctx, bodyR * 0.86, bodyY + bodyR * 0.16, radius * 0.3);
  ctx.fill();

  const dim = cls === 'mage' ? 0.55 : 1;
  ctx.globalAlpha = alpha * dim;

  if (cls === 'warrior') drawWarrior(ctx, radius, bodyY, aim, swing, colors);
  else if (cls === 'ranger') drawRanger(ctx, radius, bodyY, aim, swing, colors);
  else drawMage(ctx, radius, bodyY, aim, swing, colors);

  ctx.globalAlpha = alpha;

  // --- head
  const headY = bodyY - bodyR * 0.92;
  ctx.fillStyle = colors.head;
  ctx.strokeStyle = colors.dark;
  ctx.lineWidth = 2;
  circle(ctx, 0, headY, radius * 0.56);
  ctx.fill();
  ctx.stroke();

  if (cls === 'warrior') {
    // Helmet visor
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.arc(0, headY, radius * 0.58, Math.PI * 1.03, Math.PI * 1.97);
    ctx.fill();
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(-radius * 0.07 + look, headY - radius * 0.42);
    ctx.lineTo(radius * 0.07 + look, headY - radius * 0.42);
    ctx.lineTo(radius * 0.05 + look, headY + radius * 0.24);
    ctx.lineTo(-radius * 0.05 + look, headY + radius * 0.24);
    ctx.closePath();
    ctx.fill();
  } else if (cls === 'ranger') {
    // Hood
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.moveTo(-radius * 0.62 + look * 0.4, headY + radius * 0.5);
    ctx.quadraticCurveTo(-radius * 0.66 + look * 0.4, headY - radius * 0.95, 0, headY - radius * 0.78);
    ctx.quadraticCurveTo(radius * 0.66 + look * 0.4, headY - radius * 0.95, radius * 0.62 + look * 0.4, headY + radius * 0.5);
    ctx.closePath();
    ctx.fill();
  } else {
    // Wizard hat
    ctx.fillStyle = colors.dark;
    const tipX = -radius * 0.55 + look * 0.7;
    ctx.beginPath();
    ctx.moveTo(-radius * 0.92, headY - radius * 0.28);
    ctx.quadraticCurveTo(tipX, headY - radius * 2.2, radius * 0.94, headY - radius * 0.24);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.ellipse(0, headY - radius * 0.26, radius * 0.98, radius * 0.19, 0, 0, TAU);
    ctx.fill();
  }

  // --- eyes
  ctx.fillStyle = '#141a22';
  const eyeY = headY + radius * 0.1 + lookY * 0.4;
  const spread = radius * 0.2;
  circle(ctx, -spread + look * 0.3, eyeY, radius * 0.075);
  ctx.fill();
  circle(ctx, spread + look * 0.3, eyeY, radius * 0.075);
  ctx.fill();

  if (flash > 0) {
    ctx.globalAlpha = Math.min(1, flash) * 0.65;
    ctx.fillStyle = '#ffffff';
    circle(ctx, 0, bodyY - radius * 0.2, radius * 1.35);
    ctx.fill();
  }
  ctx.restore();
}

interface Palette {
  main: string;
  light: string;
  dark: string;
  head: string;
}

const CLASS_PALETTE: Record<ClassId, Palette> = {
  warrior: { main: '#c2603c', light: '#e8946b', dark: '#7d3721', head: '#e8bf99' },
  ranger: { main: '#4f9d55', light: '#8ed98d', dark: '#2c6033', head: '#eed0a8' },
  mage: { main: '#6c5ce7', light: '#a99bff', dark: '#3c2f96', head: '#f0d6bd' },
};

function drawWarrior(
  ctx: CanvasRenderingContext2D,
  radius: number,
  bodyY: number,
  aim: number,
  swing: number,
  colors: Palette,
): void {
  const progress = 1 - swing;
  const sweep = swing > 0 ? (0.5 - progress) * 2.1 : 0;
  const angle = aim + sweep;

  // Shield on the off-hand
  ctx.save();
  ctx.translate(Math.cos(aim - Math.PI / 2) * radius * 0.9, bodyY + Math.sin(aim - Math.PI / 2) * radius * 0.34);
  ctx.rotate(aim);
  ctx.fillStyle = '#8d949f';
  ctx.strokeStyle = '#5c636d';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(0, 0, radius * 0.62, radius * 0.82, 0, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = colors.light;
  circle(ctx, 0, 0, radius * 0.2);
  ctx.fill();
  ctx.restore();

  // Sword
  ctx.save();
  ctx.translate(Math.cos(aim - Math.PI / 2) * radius * -0.75, bodyY + Math.sin(aim - Math.PI / 2) * radius * -0.3);
  ctx.rotate(angle);
  ctx.fillStyle = '#6b4a2c';
  ctx.fillRect(-radius * 0.1, -radius * 0.16, radius * 0.5, radius * 0.3);
  ctx.fillStyle = '#cfd6e2';
  ctx.beginPath();
  ctx.moveTo(radius * 0.32, -radius * 0.11);
  ctx.lineTo(radius * 1.5, -radius * 0.06);
  ctx.lineTo(radius * 1.66, 0);
  ctx.lineTo(radius * 1.5, radius * 0.06);
  ctx.lineTo(radius * 0.32, radius * 0.11);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.fillRect(radius * 0.4, -radius * 0.06, radius * 1.1, radius * 0.045);
  ctx.restore();
}

function drawRanger(
  ctx: CanvasRenderingContext2D,
  radius: number,
  bodyY: number,
  aim: number,
  swing: number,
  colors: Palette,
): void {
  // Quiver on the back
  ctx.save();
  ctx.translate(-Math.cos(aim) * radius * 0.7, bodyY + radius * 0.3);
  ctx.rotate(aim * 0.3 - 0.5);
  ctx.fillStyle = '#5a4126';
  ctx.beginPath();
  ctx.roundRect(-radius * 0.18, -radius * 0.5, radius * 0.36, radius * 1, 3);
  ctx.fill();
  ctx.fillStyle = '#d8d2c0';
  for (let i = -1; i <= 1; i++) {
    ctx.fillRect(i * radius * 0.1 - radius * 0.02, -radius * 0.82, radius * 0.05, radius * 0.4);
  }
  ctx.restore();

  // Bow held towards the aim direction
  const pull = swing * radius * 0.45;
  ctx.save();
  ctx.translate(Math.cos(aim) * radius * 0.72, bodyY + Math.sin(aim) * radius * 0.5);
  ctx.rotate(aim);
  ctx.strokeStyle = '#8a6a3c';
  ctx.lineWidth = Math.max(2.4, radius * 0.16);
  ctx.beginPath();
  ctx.arc(0, 0, radius * 0.95, -Math.PI * 0.62, Math.PI * 0.62);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(240, 240, 240, 0.85)';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(radius * 0.36, -radius * 0.9);
  ctx.lineTo(-pull, 0);
  ctx.lineTo(radius * 0.36, radius * 0.9);
  ctx.stroke();
  ctx.fillStyle = colors.light;
  ctx.fillRect(swing > 0 ? -pull - radius * 0.1 : -radius * 0.34, -radius * 0.05, radius * 0.6, radius * 0.1);
  ctx.restore();
}

function drawMage(
  ctx: CanvasRenderingContext2D,
  radius: number,
  bodyY: number,
  aim: number,
  swing: number,
  colors: Palette,
): void {
  // Robe
  ctx.fillStyle = colors.main;
  ctx.beginPath();
  ctx.moveTo(-radius * 0.95, -radius * 0.1);
  ctx.lineTo(radius * 0.95, -radius * 0.1);
  ctx.lineTo(radius * 0.6, bodyY + radius * 0.55);
  ctx.lineTo(-radius * 0.6, bodyY + radius * 0.55);
  ctx.closePath();
  ctx.fill();

  // Staff
  const angle = aim + swing * 0.5;
  ctx.save();
  ctx.translate(Math.cos(aim) * radius * 0.55, bodyY + Math.sin(aim) * radius * 0.4);
  ctx.rotate(angle);
  ctx.fillStyle = '#6a4f33';
  ctx.fillRect(-radius * 0.09, -radius * 0.09, radius * 1.5, radius * 0.18);

  const glow = 0.55 + swing * 0.75;
  const orbX = radius * 1.45;
  const gradient = ctx.createRadialGradient(orbX, 0, 0, orbX, 0, radius * 0.62);
  gradient.addColorStop(0, `rgba(226, 210, 255, ${0.95 * glow})`);
  gradient.addColorStop(0.5, `rgba(150, 120, 255, ${0.6 * glow})`);
  gradient.addColorStop(1, 'rgba(110, 80, 220, 0)');
  ctx.fillStyle = gradient;
  circle(ctx, orbX, 0, radius * 0.62);
  ctx.fill();
  ctx.fillStyle = '#efe6ff';
  circle(ctx, orbX, 0, radius * 0.18);
  ctx.fill();
  ctx.restore();
}

/* ------------------------------------------------------------------------ enemies */

export function drawEnemyBody(
  ctx: CanvasRenderingContext2D,
  kind: EnemyKind,
  aim: number,
  t: number,
  radius: number,
  options: BodyOptions & { attacking: boolean },
): void {
  const { moving, flash, alpha } = options;
  ctx.save();
  ctx.globalAlpha = alpha;
  drawShadow(ctx, radius, 1, 0.3 * alpha);

  switch (kind) {
    case 'slime':
      drawSlime(ctx, radius, t, aim, moving);
      break;
    case 'boar':
      drawBoar(ctx, radius, t, aim, moving);
      break;
    case 'imp':
      drawImp(ctx, radius, t, aim, moving);
      break;
    case 'wisp':
      drawWisp(ctx, radius, t, aim);
      break;
    case 'brute':
      drawBrute(ctx, radius, t, aim, moving, options);
      break;
    case 'wolf':
      drawWolf(ctx, radius, t, aim, moving);
      break;
    case 'skeleton':
      drawSkeleton(ctx, radius, t, aim, moving);
      break;
    case 'cultist':
      drawCultist(ctx, radius, t, aim, moving);
      break;
    case 'golem':
      drawGolem(ctx, radius, t, aim, moving);
      break;
    case 'harpy':
      drawHarpy(ctx, radius, t, aim, moving);
      break;
    case 'ogre':
      drawOgre(ctx, radius, t, aim, moving);
      break;
    case 'wraith':
      drawWraith(ctx, radius, t, aim, moving);
      break;
    case 'dragonkin':
      drawDragonkin(ctx, radius, t, aim, moving);
      break;
    case 'boss':
      drawBoss(ctx, radius, t, aim, moving, options);
      break;
  }

  if (flash > 0) {
    ctx.globalAlpha = Math.min(1, flash) * 0.6;
    ctx.fillStyle = '#ffffff';
    circle(ctx, 0, -radius * 0.85, radius * 1.35);
    ctx.fill();
  }
  ctx.restore();
}

function eyes(
  ctx: CanvasRenderingContext2D,
  y: number,
  spread: number,
  size: number,
  lookX: number,
  lookY: number,
): void {
  ctx.fillStyle = '#f4f7ff';
  circle(ctx, -spread + lookX, y, size);
  ctx.fill();
  circle(ctx, spread + lookX, y, size);
  ctx.fill();
  ctx.fillStyle = '#161d27';
  circle(ctx, -spread + lookX * 1.6, y + lookY, size * 0.52);
  ctx.fill();
  circle(ctx, spread + lookX * 1.6, y + lookY, size * 0.52);
  ctx.fill();
}

function drawSlime(ctx: CanvasRenderingContext2D, r: number, t: number, aim: number, moving: number): void {
  const squash = 1 + Math.sin(t * 5.5) * 0.08;
  const hop = Math.abs(Math.sin(t * 5.5)) * r * (0.1 + moving * 0.35);
  ctx.translate(0, -hop);

  ctx.fillStyle = '#3f9c7d';
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.7 * squash, r * 1.1, r * 1.05 * squash, 0, 0, TAU);
  ctx.fill();

  ctx.fillStyle = '#57c9a4';
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.68 * squash, r * 0.98, r * 0.9 * squash, 0, 0, TAU);
  ctx.fill();

  ctx.fillStyle = 'rgba(220, 255, 245, 0.5)';
  ctx.beginPath();
  ctx.ellipse(-r * 0.32, -r * 1.2, r * 0.26, r * 0.16, -0.6, 0, TAU);
  ctx.fill();

  eyes(ctx, -r * 0.8, r * 0.36, r * 0.19, Math.cos(aim) * r * 0.14, Math.sin(aim) * r * 0.1);
}

function drawBoar(ctx: CanvasRenderingContext2D, r: number, t: number, aim: number, moving: number): void {
  const bob = Math.sin(t * 12) * moving * r * 0.1;
  ctx.translate(0, -bob);

  ctx.strokeStyle = '#6b4423';
  ctx.lineWidth = r * 0.22;
  ctx.lineCap = 'round';
  for (let i = 0; i < 3; i++) {
    const phase = t * 14 + i * 2.1;
    ctx.beginPath();
    ctx.moveTo(-r * 0.6 + i * r * 0.6, -r * 0.3);
    ctx.lineTo(-r * 0.6 + i * r * 0.6 - Math.sin(phase) * r * 0.16 * (0.4 + moving), -r * 0.02);
    ctx.stroke();
  }

  ctx.fillStyle = '#8c5c33';
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.85, r * 1.18, r * 0.86, 0, 0, TAU);
  ctx.fill();

  ctx.fillStyle = '#a8703f';
  ctx.beginPath();
  ctx.ellipse(r * 0.1, -r * 0.92, r * 1.02, r * 0.72, 0, 0, TAU);
  ctx.fill();

  const look = Math.cos(aim) * r * 0.2;
  // Snout
  ctx.fillStyle = '#e8c9a4';
  ctx.beginPath();
  ctx.ellipse(r * 0.72 + look, -r * 0.72, r * 0.42, r * 0.34, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#5a3a22';
  circle(ctx, r * 0.72 + look, -r * 0.78, r * 0.09);
  ctx.fill();
  circle(ctx, r * 0.72 + look, -r * 0.66, r * 0.09);
  ctx.fill();

  // Tusks
  ctx.strokeStyle = '#f4ecd8';
  ctx.lineWidth = Math.max(2, r * 0.14);
  ctx.beginPath();
  ctx.moveTo(r * 0.86 + look, -r * 0.6);
  ctx.quadraticCurveTo(r * 1.2 + look, -r * 0.78, r * 1.24 + look, -r * 1.02);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(r * 0.86 + look, -r * 0.86);
  ctx.quadraticCurveTo(r * 1.2 + look, -r * 0.98, r * 1.2 + look, -r * 1.2);
  ctx.stroke();

  // Ears
  ctx.fillStyle = '#6b4423';
  ctx.beginPath();
  ctx.moveTo(-r * 0.1, -r * 1.5);
  ctx.lineTo(-r * 0.5, -r * 1.85);
  ctx.lineTo(-r * 0.56, -r * 1.38);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(r * 0.36, -r * 1.5);
  ctx.lineTo(r * 0.5, -r * 1.92);
  ctx.lineTo(r * 0.72, -r * 1.42);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#241309';
  circle(ctx, -r * 0.24 + look * 0.5, -r * 1.06, r * 0.11);
  ctx.fill();
  circle(ctx, r * 0.36 + look * 0.5, -r * 1.06, r * 0.11);
  ctx.fill();
}

function drawImp(
  ctx: CanvasRenderingContext2D,
  r: number,
  t: number,
  aim: number,
  moving: number,
): void {
  const flap = Math.sin(t * 16) * 0.5;
  ctx.translate(0, -Math.abs(Math.sin(t * 8)) * r * (0.1 + moving * 0.3));

  // Wings
  ctx.fillStyle = 'rgba(120, 30, 30, 0.9)';
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(side * r * 0.6, -r * 1.0);
    ctx.rotate(side * (0.5 + flap * 0.4));
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(side * r * 1.3, -r * 0.7, side * r * 1.5, r * 0.1);
    ctx.quadraticCurveTo(side * r * 0.9, r * 0.15, 0, r * 0.4);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  // Body
  ctx.fillStyle = '#a5301f';
  circle(ctx, 0, -r * 0.85, r * 0.86);
  ctx.fill();
  ctx.fillStyle = '#c0392b';
  circle(ctx, 0, -r * 0.88, r * 0.74);
  ctx.fill();

  // Horns
  ctx.fillStyle = '#f0e2cf';
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(side * r * 0.3, -r * 1.45);
    ctx.lineTo(side * r * 0.62, -r * 2.05);
    ctx.lineTo(side * r * 0.66, -r * 1.32);
    ctx.closePath();
    ctx.fill();
  }

  // Tail
  ctx.strokeStyle = '#8a2618';
  ctx.lineWidth = Math.max(1.6, r * 0.1);
  ctx.beginPath();
  ctx.moveTo(0, -r * 0.5);
  ctx.quadraticCurveTo(-Math.cos(aim) * r * 0.8, -r * 0.1, -Math.cos(aim) * r * 1.1, -r * 0.5 + Math.sin(t * 6) * r * 0.2);
  ctx.stroke();

  eyes(ctx, -r * 1.0, r * 0.3, r * 0.16, Math.cos(aim) * r * 0.12, Math.sin(aim) * r * 0.08);
}

function drawWisp(ctx: CanvasRenderingContext2D, r: number, t: number, aim: number): void {
  const pulse = 0.85 + Math.sin(t * 3.4) * 0.15;
  const float = Math.sin(t * 1.8) * r * 0.25;
  ctx.translate(0, -r * 1.1 + float);

  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 3 * pulse);
  gradient.addColorStop(0, 'rgba(230, 250, 255, 0.95)');
  gradient.addColorStop(0.22, 'rgba(120, 210, 255, 0.75)');
  gradient.addColorStop(0.55, 'rgba(60, 150, 240, 0.28)');
  gradient.addColorStop(1, 'rgba(40, 110, 220, 0)');
  ctx.fillStyle = gradient;
  circle(ctx, 0, 0, r * 3 * pulse);
  ctx.fill();

  ctx.fillStyle = '#eafaff';
  circle(ctx, 0, 0, r * 0.5 * pulse);
  ctx.fill();

  // Orbiting motes
  ctx.fillStyle = 'rgba(180, 235, 255, 0.85)';
  for (let i = 0; i < 3; i++) {
    const a = t * 2.2 + (i / 3) * TAU;
    circle(ctx, Math.cos(a) * r * 1.3, Math.sin(a) * r * 0.7, r * 0.12);
    ctx.fill();
  }

  eyes(ctx, -r * 0.06, r * 0.22, r * 0.11, Math.cos(aim) * r * 0.1, Math.sin(aim) * r * 0.08);
}

function drawWolf(ctx: CanvasRenderingContext2D, r: number, t: number, aim: number, moving: number): void {
  const bob = Math.sin(t * 12) * moving * r * 0.09;
  ctx.translate(0, -bob);

  ctx.strokeStyle = '#5b616b';
  ctx.lineWidth = r * 0.2;
  ctx.lineCap = 'round';
  for (let i = 0; i < 3; i++) {
    const phase = t * 15 + i * 2.1;
    ctx.beginPath();
    ctx.moveTo(-r * 0.6 + i * r * 0.6, -r * 0.3);
    ctx.lineTo(-r * 0.6 + i * r * 0.6 - Math.sin(phase) * r * 0.14 * (0.4 + moving), -r * 0.02);
    ctx.stroke();
  }

  // Tail
  ctx.strokeStyle = '#6b727c';
  ctx.lineWidth = r * 0.18;
  ctx.beginPath();
  ctx.moveTo(-r * 1.0, -r * 0.9);
  ctx.quadraticCurveTo(-r * 1.6, -r * 1.1 + Math.sin(t * 6) * r * 0.15, -r * 1.5, -r * 0.6);
  ctx.stroke();

  ctx.fillStyle = '#7e838d';
  ctx.beginPath();
  ctx.ellipse(-r * 0.15, -r * 0.9, r * 1.05, r * 0.62, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#8a8f98';
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.95, r * 0.95, r * 0.55, 0, 0, TAU);
  ctx.fill();

  const look = Math.cos(aim) * r * 0.2;
  ctx.fillStyle = '#8a8f98';
  ctx.beginPath();
  ctx.ellipse(r * 0.85 + look, -r * 1.05, r * 0.45, r * 0.4, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#6b727c';
  ctx.beginPath();
  ctx.ellipse(r * 1.18 + look, -r * 0.95, r * 0.22, r * 0.16, 0, 0, TAU);
  ctx.fill();
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(r * 0.7 + look + s * r * 0.12, -r * 1.4);
    ctx.lineTo(r * 0.78 + look + s * r * 0.2, -r * 1.75);
    ctx.lineTo(r * 0.95 + look + s * r * 0.05, -r * 1.38);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillStyle = '#ffd166';
  circle(ctx, r * 0.78 + look, -r * 1.12, r * 0.07);
  ctx.fill();
  circle(ctx, r * 1.0 + look, -r * 1.1, r * 0.07);
  ctx.fill();
}

function drawSkeleton(ctx: CanvasRenderingContext2D, r: number, t: number, aim: number, moving: number): void {
  const bob = Math.sin(t * 8) * moving * r * 0.06;
  ctx.translate(0, -bob);

  ctx.strokeStyle = '#b9b294';
  ctx.lineWidth = r * 0.22;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-r * 0.25, -r * 0.55);
  ctx.lineTo(-r * 0.3, 0);
  ctx.moveTo(r * 0.25, -r * 0.55);
  ctx.lineTo(r * 0.3, 0);
  ctx.stroke();

  ctx.strokeStyle = '#e6e0c8';
  ctx.lineWidth = r * 0.16;
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.7);
  ctx.lineTo(0, -r * 0.6);
  ctx.stroke();
  ctx.lineWidth = r * 0.1;
  for (let i = 0; i < 4; i++) {
    const y = -r * 1.5 + i * r * 0.24;
    ctx.beginPath();
    ctx.moveTo(-r * 0.4, y);
    ctx.quadraticCurveTo(0, y + r * 0.1, r * 0.4, y);
    ctx.stroke();
  }

  const swing = Math.sin(t * 6) * moving * r * 0.2;
  ctx.strokeStyle = '#cfc9b0';
  ctx.lineWidth = r * 0.14;
  ctx.beginPath();
  ctx.moveTo(-r * 0.4, -r * 1.5);
  ctx.lineTo(-r * 0.55, -r * 1.0 + swing);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(r * 0.4, -r * 1.5);
  ctx.lineTo(r * 0.55, -r * 1.0 - swing);
  ctx.stroke();

  ctx.fillStyle = '#f2ecd6';
  circle(ctx, 0, -r * 1.9, r * 0.42);
  ctx.fill();
  ctx.fillStyle = '#2a2a24';
  circle(ctx, -r * 0.16, -r * 1.94, r * 0.1);
  ctx.fill();
  circle(ctx, r * 0.16, -r * 1.94, r * 0.1);
  ctx.fill();
  ctx.fillStyle = '#cfc9b0';
  ctx.fillRect(-r * 0.28, -r * 1.7, r * 0.56, r * 0.1);
}

function drawCultist(ctx: CanvasRenderingContext2D, r: number, t: number, _aim: number, moving: number): void {
  const float = Math.sin(t * 2.4) * r * 0.06 + Math.sin(t * 8) * moving * r * 0.03;
  ctx.translate(0, -float);

  ctx.fillStyle = '#5a3a86';
  ctx.beginPath();
  ctx.moveTo(-r * 0.8, -r * 0.5);
  ctx.lineTo(r * 0.8, -r * 0.5);
  ctx.lineTo(r * 0.5, -r * 1.8);
  ctx.lineTo(-r * 0.5, -r * 1.8);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#43296b';
  ctx.beginPath();
  ctx.arc(0, -r * 1.9, r * 0.5, Math.PI, 0);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-r * 0.5, -r * 1.9);
  ctx.quadraticCurveTo(-r * 0.6, -r * 1.2, -r * 0.24, -r * 1.1);
  ctx.lineTo(r * 0.24, -r * 1.1);
  ctx.quadraticCurveTo(r * 0.6, -r * 1.2, r * 0.5, -r * 1.9);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#d3b6ff';
  circle(ctx, -r * 0.16, -r * 1.9, r * 0.08);
  ctx.fill();
  circle(ctx, r * 0.16, -r * 1.9, r * 0.08);
  ctx.fill();

  ctx.strokeStyle = '#6b4f2a';
  ctx.lineWidth = r * 0.12;
  ctx.beginPath();
  ctx.moveTo(r * 0.7, -r * 1.9);
  ctx.lineTo(r * 0.7, -r * 0.1);
  ctx.stroke();
  const orb = ctx.createRadialGradient(r * 0.7, -r * 2.0, 0, r * 0.7, -r * 2.0, r * 0.4);
  orb.addColorStop(0, '#e6d0ff');
  orb.addColorStop(1, 'rgba(150, 90, 220, 0)');
  ctx.fillStyle = orb;
  circle(ctx, r * 0.7, -r * 2.0, r * 0.4);
  ctx.fill();
}

function drawGolem(ctx: CanvasRenderingContext2D, r: number, t: number, _aim: number, moving: number): void {
  const bob = Math.sin(t * 5) * moving * r * 0.05;
  ctx.translate(0, -bob);

  ctx.fillStyle = '#61666f';
  ctx.beginPath();
  ctx.roundRect(-r * 0.8, -r * 0.6, r * 0.6, r * 0.6, r * 0.14);
  ctx.roundRect(r * 0.2, -r * 0.6, r * 0.6, r * 0.6, r * 0.14);
  ctx.fill();

  ctx.fillStyle = '#7f8590';
  ctx.beginPath();
  ctx.roundRect(-r * 1.05, -r * 2.0, r * 2.1, r * 1.55, r * 0.3);
  ctx.fill();
  ctx.strokeStyle = '#565b63';
  ctx.lineWidth = 2.4;
  ctx.stroke();

  ctx.fillStyle = '#6f7683';
  ctx.beginPath();
  ctx.roundRect(-r * 1.5, -r * 1.8, r * 0.5, r * 1.2, r * 0.16);
  ctx.roundRect(r * 1.0, -r * 1.8, r * 0.5, r * 1.2, r * 0.16);
  ctx.fill();

  ctx.fillStyle = '#8b93a1';
  ctx.beginPath();
  ctx.roundRect(-r * 0.5, -r * 2.6, r * 1.0, r * 0.7, r * 0.16);
  ctx.fill();

  ctx.fillStyle = '#9ad7ff';
  ctx.fillRect(-r * 0.3, -r * 2.35, r * 0.2, r * 0.14);
  ctx.fillRect(r * 0.1, -r * 2.35, r * 0.2, r * 0.14);
}

function drawHarpy(ctx: CanvasRenderingContext2D, r: number, t: number, _aim: number, moving: number): void {
  const flap = Math.sin(t * 14) * 0.6;
  const float = Math.sin(t * 2.2) * r * 0.2 + Math.sin(t * 9) * moving * r * 0.05;
  ctx.translate(0, -float);

  ctx.fillStyle = 'rgba(50, 120, 115, 0.9)';
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(side * r * 0.5, -r * 1.2);
    ctx.rotate(side * (0.4 + flap * 0.4));
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(side * r * 1.6, -r * 0.8, side * r * 1.9, r * 0.2);
    ctx.quadraticCurveTo(side * r * 1.0, r * 0.1, 0, r * 0.4);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  ctx.fillStyle = '#3fa6a0';
  ctx.beginPath();
  ctx.ellipse(0, -r * 1.0, r * 0.55, r * 0.75, 0, 0, TAU);
  ctx.fill();

  ctx.strokeStyle = '#c8a24a';
  ctx.lineWidth = r * 0.12;
  ctx.beginPath();
  ctx.moveTo(-r * 0.2, -r * 0.4);
  ctx.lineTo(-r * 0.25, -r * 0.05);
  ctx.moveTo(r * 0.2, -r * 0.4);
  ctx.lineTo(r * 0.25, -r * 0.05);
  ctx.stroke();

  ctx.fillStyle = '#bff0ea';
  circle(ctx, 0, -r * 1.75, r * 0.36);
  ctx.fill();
  ctx.fillStyle = '#2e8a84';
  ctx.beginPath();
  ctx.moveTo(0, -r * 2.1);
  ctx.lineTo(-r * 0.3, -r * 2.5);
  ctx.lineTo(r * 0.3, -r * 2.5);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#123038';
  circle(ctx, -r * 0.13, -r * 1.78, r * 0.07);
  ctx.fill();
  circle(ctx, r * 0.13, -r * 1.78, r * 0.07);
  ctx.fill();
}

function drawOgre(ctx: CanvasRenderingContext2D, r: number, t: number, _aim: number, moving: number): void {
  const bob = Math.sin(t * 6) * (0.4 + moving) * r * 0.08;
  ctx.translate(0, -bob);

  ctx.fillStyle = '#4f5a26';
  ctx.beginPath();
  ctx.roundRect(-r * 0.75, -r * 0.5, r * 0.6, r * 0.55, r * 0.16);
  ctx.roundRect(r * 0.15, -r * 0.5, r * 0.6, r * 0.55, r * 0.16);
  ctx.fill();

  ctx.fillStyle = '#6f7a3a';
  ctx.beginPath();
  ctx.roundRect(-r * 1.05, -r * 1.95, r * 2.1, r * 1.6, r * 0.4);
  ctx.fill();
  ctx.strokeStyle = '#49521f';
  ctx.lineWidth = 2.6;
  ctx.stroke();

  ctx.fillStyle = '#8a9550';
  ctx.beginPath();
  ctx.ellipse(0, -r * 1.1, r * 0.6, r * 0.45, 0, 0, TAU);
  ctx.fill();

  ctx.strokeStyle = '#6f7a3a';
  ctx.lineWidth = r * 0.4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-r * 1.0, -r * 1.5);
  ctx.lineTo(-r * 1.35, -r * 0.7);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(r * 1.0, -r * 1.5);
  ctx.lineTo(r * 1.35, -r * 0.7);
  ctx.stroke();

  ctx.fillStyle = '#7f8a44';
  circle(ctx, 0, -r * 2.15, r * 0.5);
  ctx.fill();
  ctx.strokeStyle = '#f0e6c8';
  ctx.lineWidth = r * 0.1;
  ctx.beginPath();
  ctx.moveTo(-r * 0.25, -r * 2.0);
  ctx.lineTo(-r * 0.3, -r * 1.7);
  ctx.moveTo(r * 0.25, -r * 2.0);
  ctx.lineTo(r * 0.3, -r * 1.7);
  ctx.stroke();
  ctx.fillStyle = '#2a2a12';
  circle(ctx, -r * 0.16, -r * 2.2, r * 0.08);
  ctx.fill();
  circle(ctx, r * 0.16, -r * 2.2, r * 0.08);
  ctx.fill();
}

function drawWraith(ctx: CanvasRenderingContext2D, r: number, t: number, _aim: number, moving: number): void {
  const float = Math.sin(t * 1.6) * r * 0.3 + Math.sin(t * 7) * moving * r * 0.04;
  ctx.translate(0, -r * 0.6 + float);

  const haze = ctx.createRadialGradient(0, -r * 1.0, 0, 0, -r * 1.0, r * 2.2);
  haze.addColorStop(0, 'rgba(200, 245, 255, 0.5)');
  haze.addColorStop(1, 'rgba(80, 180, 220, 0)');
  ctx.fillStyle = haze;
  circle(ctx, 0, -r * 1.0, r * 2.2);
  ctx.fill();

  ctx.fillStyle = 'rgba(60, 120, 150, 0.88)';
  ctx.beginPath();
  ctx.moveTo(-r * 0.7, -r * 0.4);
  ctx.quadraticCurveTo(-r * 0.5, -r * 2.2, 0, -r * 2.3);
  ctx.quadraticCurveTo(r * 0.5, -r * 2.2, r * 0.7, -r * 0.4);
  ctx.lineTo(r * 0.5, -r * 0.2);
  ctx.lineTo(r * 0.3, -r * 0.45);
  ctx.lineTo(r * 0.1, -r * 0.15);
  ctx.lineTo(-r * 0.1, -r * 0.5);
  ctx.lineTo(-r * 0.3, -r * 0.15);
  ctx.lineTo(-r * 0.5, -r * 0.45);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'rgba(10, 20, 30, 0.85)';
  circle(ctx, 0, -r * 1.7, r * 0.4);
  ctx.fill();
  ctx.fillStyle = '#bff0ff';
  circle(ctx, -r * 0.14, -r * 1.72, r * 0.08);
  ctx.fill();
  circle(ctx, r * 0.14, -r * 1.72, r * 0.08);
  ctx.fill();
}

function drawDragonkin(ctx: CanvasRenderingContext2D, r: number, t: number, _aim: number, moving: number): void {
  const bob = Math.sin(t * 5) * (0.4 + moving) * r * 0.06;
  ctx.translate(0, -bob);

  ctx.strokeStyle = '#7d2b24';
  ctx.lineWidth = r * 0.24;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-r * 0.6, -r * 0.7);
  ctx.quadraticCurveTo(-r * 1.7, -r * 0.5 + Math.sin(t * 5) * r * 0.2, -r * 1.6, -r * 0.05);
  ctx.stroke();

  ctx.fillStyle = '#7d2b24';
  ctx.beginPath();
  ctx.roundRect(-r * 0.8, -r * 0.55, r * 0.6, r * 0.6, r * 0.16);
  ctx.roundRect(r * 0.2, -r * 0.55, r * 0.6, r * 0.6, r * 0.16);
  ctx.fill();

  ctx.fillStyle = '#a83b32';
  ctx.beginPath();
  ctx.roundRect(-r * 0.95, -r * 1.9, r * 1.9, r * 1.5, r * 0.34);
  ctx.fill();
  ctx.fillStyle = '#f0c060';
  ctx.beginPath();
  ctx.ellipse(0, -r * 1.05, r * 0.5, r * 0.4, 0, 0, TAU);
  ctx.fill();

  ctx.strokeStyle = '#a83b32';
  ctx.lineWidth = r * 0.34;
  ctx.beginPath();
  ctx.moveTo(-r * 0.9, -r * 1.5);
  ctx.lineTo(-r * 1.2, -r * 0.7);
  ctx.moveTo(r * 0.9, -r * 1.5);
  ctx.lineTo(r * 1.2, -r * 0.7);
  ctx.stroke();

  ctx.fillStyle = 'rgba(125, 43, 36, 0.85)';
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(side * r * 0.8, -r * 1.7);
    ctx.quadraticCurveTo(side * r * 1.8, -r * 2.4, side * r * 1.9, -r * 1.2);
    ctx.quadraticCurveTo(side * r * 1.2, -r * 1.3, side * r * 0.8, -r * 1.5);
    ctx.closePath();
    ctx.fill();
  }

  ctx.fillStyle = '#b8453a';
  circle(ctx, 0, -r * 2.05, r * 0.45);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(0, -r * 2.1);
  ctx.quadraticCurveTo(r * 0.5, -r * 1.95, r * 0.55, -r * 1.7);
  ctx.lineTo(-r * 0.1, -r * 1.8);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#f0c060';
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(s * r * 0.3, -r * 2.3);
    ctx.lineTo(s * r * 0.55, -r * 2.75);
    ctx.lineTo(s * r * 0.6, -r * 2.15);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillStyle = '#ffe08a';
  circle(ctx, -r * 0.15, -r * 2.1, r * 0.07);
  ctx.fill();
  circle(ctx, r * 0.15, -r * 2.1, r * 0.07);
  ctx.fill();
}

function drawBrute(
  ctx: CanvasRenderingContext2D,
  r: number,
  t: number,
  aim: number,
  moving: number,
  options: { attacking: boolean },
): void {
  const bob = Math.sin(t * 6) * (0.4 + moving) * r * 0.09;
  ctx.translate(0, -bob);
  const windupSquash = options.attacking ? 1.08 : 1;

  // Legs
  ctx.fillStyle = '#4b3570';
  ctx.beginPath();
  ctx.roundRect(-r * 0.72, -r * 0.5, r * 0.5, r * 0.55, r * 0.16);
  ctx.roundRect(r * 0.22, -r * 0.5, r * 0.5, r * 0.55, r * 0.16);
  ctx.fill();

  // Torso
  ctx.fillStyle = '#7d5ba6';
  ctx.beginPath();
  ctx.roundRect(-r * 1.0, -r * 1.95 * windupSquash, r * 2.0, r * 1.6 * windupSquash, r * 0.42);
  ctx.fill();
  ctx.strokeStyle = '#4b3570';
  ctx.lineWidth = 2.4;
  ctx.stroke();

  // Shoulders
  ctx.fillStyle = '#9a78c4';
  circle(ctx, -r * 1.02, -r * 1.72, r * 0.46);
  ctx.fill();
  circle(ctx, r * 1.02, -r * 1.72, r * 0.46);
  ctx.fill();

  // Arms raised when winding up
  const armLift = options.attacking ? -r * 0.5 : 0;
  ctx.strokeStyle = '#7d5ba6';
  ctx.lineWidth = r * 0.36;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-r * 0.95, -r * 1.5);
  ctx.lineTo(-r * 1.2, -r * 0.85 + armLift);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(r * 0.95, -r * 1.5);
  ctx.lineTo(r * 1.2, -r * 0.85 + armLift);
  ctx.stroke();

  // Head
  ctx.fillStyle = '#8c68b8';
  circle(ctx, 0, -r * 1.35, r * 0.58);
  ctx.fill();

  ctx.fillStyle = options.attacking ? '#ffd166' : '#ff8a5c';
  const look = Math.cos(aim) * r * 0.16;
  circle(ctx, -r * 0.2 + look, -r * 1.4, r * 0.12);
  ctx.fill();
  circle(ctx, r * 0.2 + look, -r * 1.4, r * 0.12);
  ctx.fill();
}

/** A world boss: a hulking crowned brute, deliberately larger than anything else. */
function drawBoss(
  ctx: CanvasRenderingContext2D,
  r: number,
  t: number,
  aim: number,
  moving: number,
  options: { attacking: boolean },
): void {
  const bob = Math.sin(t * 3.4) * (0.5 + moving) * r * 0.06;
  ctx.translate(0, -bob);
  const windup = options.attacking ? 1.06 : 1;

  // Legs
  ctx.fillStyle = '#3a1f26';
  ctx.beginPath();
  ctx.roundRect(-r * 0.82, -r * 0.55, r * 0.6, r * 0.6, r * 0.18);
  ctx.roundRect(r * 0.22, -r * 0.55, r * 0.6, r * 0.6, r * 0.18);
  ctx.fill();

  // Torso
  ctx.fillStyle = '#7a2630';
  ctx.beginPath();
  ctx.roundRect(-r * 1.1, -r * 2.0 * windup, r * 2.2, r * 1.65 * windup, r * 0.4);
  ctx.fill();
  ctx.strokeStyle = '#43141b';
  ctx.lineWidth = 3;
  ctx.stroke();

  // Glowing chest rune
  ctx.strokeStyle = 'rgba(255, 200, 120, 0.85)';
  ctx.lineWidth = Math.max(2, r * 0.08);
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.75);
  ctx.lineTo(0, -r * 1.05);
  ctx.moveTo(-r * 0.28, -r * 1.45);
  ctx.lineTo(r * 0.28, -r * 1.25);
  ctx.stroke();

  // Shoulders with gold studs
  ctx.fillStyle = '#9a3038';
  circle(ctx, -r * 1.15, -r * 1.75, r * 0.55);
  ctx.fill();
  circle(ctx, r * 1.15, -r * 1.75, r * 0.55);
  ctx.fill();
  ctx.fillStyle = '#e5c96a';
  circle(ctx, -r * 1.15, -r * 1.95, r * 0.2);
  ctx.fill();
  circle(ctx, r * 1.15, -r * 1.95, r * 0.2);
  ctx.fill();

  // Arms, raised on the wind-up
  const lift = options.attacking ? -r * 0.6 : 0;
  ctx.strokeStyle = '#7a2630';
  ctx.lineWidth = r * 0.42;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-r * 1.05, -r * 1.55);
  ctx.lineTo(-r * 1.35, -r * 0.8 + lift);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(r * 1.05, -r * 1.55);
  ctx.lineTo(r * 1.35, -r * 0.8 + lift);
  ctx.stroke();

  // Head
  ctx.fillStyle = '#8c3038';
  circle(ctx, 0, -r * 1.4, r * 0.62);
  ctx.fill();

  // Crown of horns
  ctx.fillStyle = '#e5c96a';
  for (const off of [-0.4, -0.13, 0.13, 0.4]) {
    ctx.beginPath();
    ctx.moveTo(r * off - r * 0.09, -r * 1.82);
    ctx.lineTo(r * off + r * 0.09, -r * 1.82);
    ctx.lineTo(r * off, -r * 2.38);
    ctx.closePath();
    ctx.fill();
  }

  // Eyes that flare during the wind-up
  const look = Math.cos(aim) * r * 0.14;
  ctx.fillStyle = options.attacking ? '#fff2b0' : '#ffb347';
  circle(ctx, -r * 0.22 + look, -r * 1.45, r * 0.14);
  ctx.fill();
  circle(ctx, r * 0.22 + look, -r * 1.45, r * 0.14);
  ctx.fill();
}

/* ------------------------------------------------------------------------ trader */

/** The merchant in the sanctuary. Sells potions and scrolls for coin. */
/** Shared look for every city vendor: a counter, some wares, and a hooded figure. */
interface VendorStyle {
  robe: string;
  sash: string;
  hood: string;
  hat: string;
  band: string;
  goods: 'potions' | 'weapons' | 'armour';
}

const POTION_VENDOR: VendorStyle = {
  robe: '#3b3560',
  sash: '#7d5ba6',
  hood: '#2c2848',
  hat: '#241f3d',
  band: '#e5b95c',
  goods: 'potions',
};
const WEAPON_VENDOR: VendorStyle = {
  robe: '#4a3a2a',
  sash: '#8a6a3c',
  hood: '#2f241a',
  hat: '#3a2c1c',
  band: '#c7cede',
  goods: 'weapons',
};
const ARMOUR_VENDOR: VendorStyle = {
  robe: '#35424f',
  sash: '#6f7683',
  hood: '#26303a',
  hat: '#1f2830',
  band: '#c7cede',
  goods: 'armour',
};

/** Marrow the Fence: potions and town scrolls. */
export function drawTrader(ctx: CanvasRenderingContext2D, t: number, radius = 20): void {
  drawStall(ctx, t, radius, POTION_VENDOR);
}

/** Brynja: weapons on the counter. */
export function drawWeaponsmith(ctx: CanvasRenderingContext2D, t: number, radius = 20): void {
  drawStall(ctx, t, radius, WEAPON_VENDOR);
}

/** Havard: armour and trinkets. */
export function drawArmourer(ctx: CanvasRenderingContext2D, t: number, radius = 20): void {
  drawStall(ctx, t, radius, ARMOUR_VENDOR);
}

function drawWares(ctx: CanvasRenderingContext2D, r: number, kind: VendorStyle['goods']): void {
  if (kind === 'potions') {
    for (const [ox, colour] of [
      [-r * 0.95, '#e0544c'],
      [-r * 0.35, '#4b86e0'],
      [r * 0.28, '#e0544c'],
    ] as Array<[number, string]>) {
      ctx.fillStyle = colour;
      ctx.beginPath();
      ctx.arc(ox, -r * 1.02, r * 0.16, 0, TAU);
      ctx.fill();
      ctx.fillStyle = '#d8dde8';
      ctx.fillRect(ox - r * 0.06, -r * 1.28, r * 0.12, r * 0.12);
    }
    ctx.fillStyle = '#f2c94c';
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.ellipse(r * 0.85 + i * r * 0.1, -r * 1.02 - i * 2, r * 0.17, r * 0.11, 0, 0, TAU);
      ctx.fill();
    }
    return;
  }

  if (kind === 'weapons') {
    // Two blades standing point-up, and a little coin beside them.
    for (const ox of [-r * 0.75, -r * 0.15]) {
      ctx.fillStyle = '#c7cede';
      ctx.beginPath();
      ctx.moveTo(ox, -r * 1.02);
      ctx.lineTo(ox + r * 0.16, -r * 1.02);
      ctx.lineTo(ox + r * 0.1, -r * 2.15);
      ctx.lineTo(ox + r * 0.06, -r * 2.28);
      ctx.lineTo(ox + r * 0.02, -r * 2.15);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#6b4f2a';
      ctx.fillRect(ox - r * 0.08, -r * 1.14, r * 0.32, r * 0.12);
      ctx.fillRect(ox + r * 0.02, -r * 1.0, r * 0.12, r * 0.24);
    }
    ctx.fillStyle = '#f2c94c';
    for (let i = 0; i < 2; i++) {
      ctx.beginPath();
      ctx.ellipse(r * 0.75 + i * r * 0.12, -r * 1.06 - i * 3, r * 0.16, r * 0.11, 0, 0, TAU);
      ctx.fill();
    }
    return;
  }

  // Armour: a round shield and a helm.
  ctx.fillStyle = '#8b93a1';
  ctx.beginPath();
  ctx.arc(-r * 0.55, -r * 1.35, r * 0.42, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = '#575e69';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.strokeStyle = '#c7cede';
  ctx.beginPath();
  ctx.moveTo(-r * 0.55, -r * 1.72);
  ctx.lineTo(-r * 0.55, -r * 0.98);
  ctx.moveTo(-r * 0.92, -r * 1.35);
  ctx.lineTo(-r * 0.18, -r * 1.35);
  ctx.stroke();
  ctx.fillStyle = '#7d8492';
  ctx.beginPath();
  ctx.arc(r * 0.45, -r * 1.5, r * 0.34, Math.PI, 0);
  ctx.lineTo(r * 0.79, -r * 1.28);
  ctx.lineTo(r * 0.11, -r * 1.28);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#2b303a';
  ctx.fillRect(r * 0.36, -r * 1.5, r * 0.18, r * 0.22);
}

function drawStall(ctx: CanvasRenderingContext2D, t: number, radius: number, style: VendorStyle): void {
  const bob = Math.sin(t * 1.5) * radius * 0.04;

  ctx.save();
  drawShadow(ctx, radius * 1.6, 1, 0.32);

  // Counter
  ctx.fillStyle = '#4a3324';
  ctx.beginPath();
  ctx.roundRect(-radius * 1.6, -radius * 0.8, radius * 3.2, radius * 0.92, 5);
  ctx.fill();
  ctx.fillStyle = '#6d4c34';
  ctx.beginPath();
  ctx.roundRect(-radius * 1.6, -radius * 0.92, radius * 3.2, radius * 0.42, 4);
  ctx.fill();

  drawWares(ctx, radius, style.goods);

  // Merchant
  ctx.save();
  ctx.translate(0, bob);

  ctx.fillStyle = style.robe;
  ctx.beginPath();
  ctx.moveTo(-radius * 0.85, -radius * 0.55);
  ctx.lineTo(radius * 0.85, -radius * 0.55);
  ctx.lineTo(radius * 0.58, -radius * 1.75);
  ctx.lineTo(-radius * 0.58, -radius * 1.75);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = style.sash;
  ctx.fillRect(-radius * 0.7, -radius * 1.2, radius * 1.4, radius * 0.16);

  ctx.fillStyle = '#d9b48c';
  circle(ctx, 0, -radius * 1.95, radius * 0.4);
  ctx.fill();
  ctx.fillStyle = style.hood;
  ctx.beginPath();
  ctx.arc(0, -radius * 2.0, radius * 0.52, Math.PI * 0.92, Math.PI * 2.08);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-radius * 0.52, -radius * 2.02);
  ctx.quadraticCurveTo(-radius * 0.62, -radius * 1.3, -radius * 0.24, -radius * 1.2);
  ctx.lineTo(radius * 0.24, -radius * 1.2);
  ctx.quadraticCurveTo(radius * 0.62, -radius * 1.3, radius * 0.52, -radius * 2.02);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = style.hat;
  ctx.beginPath();
  ctx.ellipse(0, -radius * 2.28, radius * 1.05, radius * 0.2, 0, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-radius * 0.5, -radius * 2.3);
  ctx.quadraticCurveTo(0, -radius * 3.1, radius * 0.5, -radius * 2.3);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = style.band;
  ctx.fillRect(-radius * 0.5, -radius * 2.42, radius * 1.0, radius * 0.12);

  ctx.fillStyle = '#ffd977';
  const blink = Math.sin(t * 0.9) > 0.96 ? 0.3 : 1;
  ctx.beginPath();
  ctx.ellipse(-radius * 0.15, -radius * 1.98, radius * 0.07, radius * 0.07 * blink, 0, 0, TAU);
  ctx.ellipse(radius * 0.15, -radius * 1.98, radius * 0.07, radius * 0.07 * blink, 0, 0, TAU);
  ctx.fill();

  ctx.restore();
  ctx.restore();
}

/* -------------------------------------------------------------------------- loot */

/** A coin pile, potion, scroll or piece of gear lying on the ground. */
export function drawDrop(
  ctx: CanvasRenderingContext2D,
  kind: 'coin' | 'health' | 'mana' | 'scroll' | 'gear',
  amount: number,
  t: number,
  seed: number,
  rarityColor?: string,
): void {
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.beginPath();
  ctx.ellipse(0, 0, 10, 3.6, 0, 0, TAU);
  ctx.fill();

  const bob = Math.sin(t * 3 + seed) * 2.2;
  ctx.translate(0, -13 + bob);

  if (kind === 'coin') {
    // Bigger piles for bigger payouts.
    const coins = amount >= 40 ? 3 : amount >= 12 ? 2 : 1;
    for (let i = 0; i < coins; i++) {
      ctx.save();
      ctx.translate((i - (coins - 1) / 2) * 6.5, i === 0 ? 0 : -5 - i * 2);
      drawItemIcon(ctx, 'coin', 23, t + seed + i);
      ctx.restore();
    }
  } else if (kind === 'gear') {
    drawGearDrop(ctx, rarityColor ?? '#9aa4b2', t + seed);
  } else {
    drawItemIcon(ctx, kind, 30, t * 0.6 + seed);
  }
  ctx.restore();
}

/** A small chest with a glow tinted by the item's rarity, so rarity is visible on the ground. */
function drawGearDrop(ctx: CanvasRenderingContext2D, color: string, t: number): void {
  const pulse = 0.5 + Math.sin(t * 3.2) * 0.2;
  const glow = ctx.createRadialGradient(0, 0, 2, 0, 0, 22);
  glow.addColorStop(0, color);
  glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.save();
  ctx.globalAlpha = pulse * 0.55;
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(0, 0, 22, 0, TAU);
  ctx.fill();
  ctx.restore();

  // Body
  ctx.fillStyle = '#6b4f2a';
  ctx.beginPath();
  ctx.roundRect(-11, -8, 22, 14, 3);
  ctx.fill();
  ctx.strokeStyle = '#3f2e18';
  ctx.lineWidth = 1.6;
  ctx.stroke();
  // Rarity-tinted lid
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(-11, -13, 22, 7, [3, 3, 0, 0]);
  ctx.fill();
  // Clasp
  ctx.fillStyle = '#e5c96a';
  ctx.fillRect(-2, -9, 4, 6);
}

/** The glowing boundary of the sanctuary. */
export function drawSafeZone(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  t: number,
): void {
  ctx.save();
  ctx.translate(x, y);

  const gradient = ctx.createRadialGradient(0, 0, r * 0.15, 0, 0, r);
  gradient.addColorStop(0, 'rgba(120, 210, 190, 0.14)');
  gradient.addColorStop(0.72, 'rgba(90, 180, 170, 0.08)');
  gradient.addColorStop(1, 'rgba(70, 150, 160, 0)');
  ctx.fillStyle = gradient;
  circle(ctx, 0, 0, r);
  ctx.fill();

  const pulse = 0.55 + Math.sin(t * 1.4) * 0.18;
  ctx.strokeStyle = `rgba(150, 240, 220, ${pulse})`;
  ctx.lineWidth = 3;
  ctx.setLineDash([18, 14]);
  ctx.lineDashOffset = -t * 26;
  circle(ctx, 0, 0, r);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.strokeStyle = `rgba(150, 240, 220, ${pulse * 0.25})`;
  ctx.lineWidth = 12;
  circle(ctx, 0, 0, r);
  ctx.stroke();

  ctx.restore();
}

/* -------------------------------------------------------------------- the city */

/** Draws any world obstacle, dispatching on its kind. */
export function drawObstacle(ctx: CanvasRenderingContext2D, o: Obstacle, t: number): void {
  switch (o.kind) {
    case 'tree':
      drawTree(ctx, o, t);
      break;
    case 'rock':
      drawRock(ctx, o);
      break;
    case 'wall':
      drawWall(ctx, o);
      break;
    case 'tower':
      drawTower(ctx, o, t);
      break;
    case 'hut':
      drawHut(ctx, o);
      break;
    case 'fountain':
      drawFountain(ctx, o, t);
      break;
    case 'crate':
      drawCrate(ctx, o);
      break;
    case 'barrel':
      drawBarrel(ctx, o);
      break;
    case 'tent':
      drawTent(ctx, o);
      break;
    case 'campfire':
      drawCampfire(ctx, o, t);
      break;
    case 'gravestone':
      drawGravestone(ctx, o);
      break;
    case 'crypt':
      drawCrypt(ctx, o);
      break;
    case 'chapel':
      drawChapel(ctx, o);
      break;
    case 'pillar':
      drawPillar(ctx, o);
      break;
    case 'monolith':
      drawMonolith(ctx, o);
      break;
    case 'fence':
      drawFence(ctx, o);
      break;
    case 'well':
      drawWell(ctx, o);
      break;
    case 'scarecrow':
      drawScarecrow(ctx, o);
      break;
  }
}

function stone(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
  face: string,
  shade: string,
): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, radius);
  ctx.fillStyle = face;
  ctx.fill();
  ctx.strokeStyle = shade;
  ctx.lineWidth = 1.6;
  ctx.stroke();
}

/** Wall stones are rotated to sit tangent to the city ring, so they read as one wall. */
function drawWall(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const angle = Math.atan2(o.y - CITY_CENTER.y, o.x - CITY_CENTER.x);
  const w = o.r * 1.02;
  const h = o.r * 0.72;

  ctx.save();
  ctx.rotate(angle + Math.PI / 2);

  ctx.fillStyle = 'rgba(0, 0, 0, 0.34)';
  ctx.beginPath();
  ctx.ellipse(0, h * 0.75, w * 1.16, h * 0.55, 0, 0, TAU);
  ctx.fill();

  stone(ctx, -w, -h * 1.5, w * 2, h * 2.4, 5, '#6f7683', '#454b55');
  stone(ctx, -w, -h * 1.5, w * 2, h * 0.75, 5, '#929aa7', '#6b7280');

  // Mortar seams, offset per stone for a bit of variety.
  const seam = Math.sin(o.x * 0.7 + o.y * 1.3) * w * 0.25;
  ctx.strokeStyle = 'rgba(60, 66, 76, 0.7)';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(seam, -h * 0.75);
  ctx.lineTo(seam, h * 0.9);
  ctx.stroke();

  ctx.restore();
}

function drawTower(ctx: CanvasRenderingContext2D, o: Obstacle, t: number): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.05, 1, 0.36);

  stone(ctx, -r, -r * 2.3, r * 2, r * 2.6, r * 0.3, '#6f7683', '#454b55');
  stone(ctx, -r, -r * 2.3, r * 2, r * 0.55, r * 0.3, '#929aa7', '#6b7280');

  // Battlements
  ctx.fillStyle = '#7d8492';
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath();
    ctx.roundRect(i * r * 0.66 - r * 0.24, -r * 2.72, r * 0.48, r * 0.48, 3);
    ctx.fill();
  }

  // Conical roof
  ctx.fillStyle = '#9c4444';
  ctx.beginPath();
  ctx.moveTo(-r * 1.12, -r * 2.62);
  ctx.lineTo(r * 1.12, -r * 2.62);
  ctx.lineTo(0, -r * 3.85);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.beginPath();
  ctx.moveTo(-r * 1.12, -r * 2.62);
  ctx.lineTo(0, -r * 2.62);
  ctx.lineTo(0, -r * 3.85);
  ctx.closePath();
  ctx.fill();

  // Pennant
  ctx.strokeStyle = '#4a4f59';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(0, -r * 3.85);
  ctx.lineTo(0, -r * 4.7);
  ctx.stroke();
  const wave = Math.sin(t * 2.4) * r * 0.16;
  ctx.fillStyle = '#e5b95c';
  ctx.beginPath();
  ctx.moveTo(0, -r * 4.66);
  ctx.quadraticCurveTo(r * 0.6, -r * 4.52 + wave, r * 0.95, -r * 4.2);
  ctx.lineTo(0, -r * 3.94);
  ctx.closePath();
  ctx.fill();

  // Arrow slit
  ctx.fillStyle = '#2b303a';
  ctx.beginPath();
  ctx.roundRect(-r * 0.13, -r * 1.7, r * 0.26, r * 0.85, 3);
  ctx.fill();
  ctx.restore();
}

function drawHut(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.1, 1, 0.34);

  // Walls
  stone(ctx, -r, -r * 1.5, r * 2, r * 1.7, r * 0.18, '#7a6a55', '#4c4133');
  ctx.fillStyle = 'rgba(255, 244, 214, 0.07)';
  ctx.beginPath();
  ctx.roundRect(-r, -r * 1.5, r * 2, r * 0.5, r * 0.18);
  ctx.fill();

  // Door
  ctx.fillStyle = '#3d3226';
  ctx.beginPath();
  ctx.roundRect(-r * 0.26, -r * 0.75, r * 0.52, r * 0.95, [r * 0.24, r * 0.24, 0, 0]);
  ctx.fill();
  ctx.fillStyle = '#c9a227';
  ctx.beginPath();
  ctx.arc(r * 0.13, -r * 0.32, r * 0.05, 0, TAU);
  ctx.fill();

  // Window
  ctx.fillStyle = '#2b303a';
  ctx.beginPath();
  ctx.roundRect(r * 0.4, -r * 1.24, r * 0.34, r * 0.34, 3);
  ctx.fill();

  // Thatch roof
  ctx.fillStyle = '#8a6a3c';
  ctx.beginPath();
  ctx.moveTo(-r * 1.24, -r * 1.44);
  ctx.lineTo(r * 1.24, -r * 1.44);
  ctx.lineTo(0, -r * 2.7);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.14)';
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.44);
  ctx.lineTo(r * 1.24, -r * 1.44);
  ctx.lineTo(0, -r * 2.7);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#6b4f2a';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-r * 1.24, -r * 1.44);
  ctx.lineTo(r * 1.24, -r * 1.44);
  ctx.stroke();

  // Chimney
  ctx.fillStyle = '#6f7683';
  ctx.beginPath();
  ctx.roundRect(r * 0.55, -r * 2.5, r * 0.34, r * 0.95, 3);
  ctx.fill();
  ctx.restore();
}

function drawFountain(ctx: CanvasRenderingContext2D, o: Obstacle, t: number): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.05, 1, 0.3);

  // Basin
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 0.72, 0, 0, TAU);
  ctx.fillStyle = '#6f7683';
  ctx.fill();
  ctx.strokeStyle = '#454b55';
  ctx.lineWidth = 2.4;
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(0, -r * 0.06, r * 0.82, r * 0.56, 0, 0, TAU);
  ctx.fillStyle = '#3f7fa8';
  ctx.fill();

  // Ripples
  ctx.strokeStyle = 'rgba(200, 235, 255, 0.4)';
  ctx.lineWidth = 1.6;
  for (let i = 0; i < 2; i++) {
    const phase = ((t * 0.5 + i * 0.5) % 1);
    ctx.globalAlpha = 1 - phase;
    ctx.beginPath();
    ctx.ellipse(0, -r * 0.06, r * 0.2 + phase * r * 0.6, (r * 0.2 + phase * r * 0.6) * 0.68, 0, 0, TAU);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Central pillar and spout
  ctx.fillStyle = '#828b99';
  ctx.beginPath();
  ctx.roundRect(-r * 0.14, -r * 1.15, r * 0.28, r * 1.1, 4);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(0, -r * 1.18, r * 0.34, r * 0.16, 0, 0, TAU);
  ctx.fill();

  // Falling water
  ctx.strokeStyle = 'rgba(190, 230, 255, 0.55)';
  ctx.lineWidth = 2;
  for (const side of [-1, 1]) {
    const wob = Math.sin(t * 6 + side) * r * 0.05;
    ctx.beginPath();
    ctx.moveTo(side * r * 0.3, -r * 1.14);
    ctx.quadraticCurveTo(side * r * 0.46 + wob, -r * 0.6, side * r * 0.3, -r * 0.1);
    ctx.stroke();
  }
  ctx.restore();
}

function drawCrate(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.05, 1, 0.32);
  const size = r * 1.8;
  stone(ctx, -size / 2, -size * 1.05, size, size, 4, '#8a6a3c', '#5b4426');
  ctx.strokeStyle = '#5b4426';
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  ctx.moveTo(-size / 2, -size * 1.05);
  ctx.lineTo(size / 2, -size * 0.05);
  ctx.moveTo(size / 2, -size * 1.05);
  ctx.lineTo(-size / 2, -size * 0.05);
  ctx.stroke();
  ctx.fillStyle = 'rgba(255, 240, 200, 0.1)';
  ctx.fillRect(-size / 2, -size * 1.05, size, size * 0.22);
  ctx.restore();
}

function drawBarrel(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.05, 1, 0.32);
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.9, r * 0.82, r, 0, 0, TAU);
  ctx.fillStyle = '#7b5a38';
  ctx.fill();
  ctx.strokeStyle = '#4d381f';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.strokeStyle = '#9aa3b0';
  ctx.lineWidth = 3;
  for (const y of [-1.42, -0.9, -0.38]) {
    ctx.beginPath();
    ctx.ellipse(0, r * y, r * 0.8, r * 0.17, 0, 0, TAU);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.ellipse(0, -r * 1.85, r * 0.72, r * 0.24, 0, 0, TAU);
  ctx.fillStyle = '#8d6a44';
  ctx.fill();
  ctx.restore();
}

/* ------------------------------------------------------- wilderness landmarks */

function drawTent(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.08, 1, 0.32);

  ctx.beginPath();
  ctx.moveTo(-r, 0);
  ctx.lineTo(r, 0);
  ctx.lineTo(0, -r * 1.7);
  ctx.closePath();
  ctx.fillStyle = '#a1784a';
  ctx.fill();
  ctx.strokeStyle = '#63492a';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Shaded half, so the ridge reads as a fold in the canvas.
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(r, 0);
  ctx.lineTo(0, -r * 1.7);
  ctx.closePath();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.16)';
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(-r * 0.34, 0);
  ctx.lineTo(r * 0.34, 0);
  ctx.lineTo(0, -r * 0.95);
  ctx.closePath();
  ctx.fillStyle = '#2f2417';
  ctx.fill();

  ctx.strokeStyle = '#4a3520';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.7);
  ctx.lineTo(0, -r * 2.05);
  ctx.stroke();
  ctx.restore();
}

function drawCampfire(ctx: CanvasRenderingContext2D, o: Obstacle, t: number): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.15, 1, 0.3);

  // Ring of stones.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * TAU;
    ctx.beginPath();
    ctx.arc(Math.cos(a) * r * 0.92, Math.sin(a) * r * 0.5, r * 0.22, 0, TAU);
    ctx.fillStyle = i % 2 ? '#6b7280' : '#565d68';
    ctx.fill();
  }

  // Charred logs.
  ctx.strokeStyle = '#5b4426';
  ctx.lineWidth = r * 0.26;
  ctx.lineCap = 'round';
  for (const a of [0.5, 1.6, 2.7]) {
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * r * 0.5, Math.sin(a) * r * 0.28 - r * 0.1);
    ctx.lineTo(-Math.cos(a) * r * 0.5, -Math.sin(a) * r * 0.28 - r * 0.1);
    ctx.stroke();
  }

  // Flickering flame, bright at the base and fading to nothing at the tip.
  const flick = Math.sin(t * 9 + o.x) * 0.15 + Math.sin(t * 13 + o.y) * 0.08;
  const h = r * 1.7 * (1 + flick);
  const glow = ctx.createLinearGradient(0, 0, 0, -h);
  glow.addColorStop(0, '#ffd166');
  glow.addColorStop(0.55, '#ff7a2f');
  glow.addColorStop(1, 'rgba(255, 60, 0, 0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.moveTo(-r * 0.5, -r * 0.15);
  ctx.quadraticCurveTo(-r * 0.62, -h * 0.6, 0, -h);
  ctx.quadraticCurveTo(r * 0.62, -h * 0.6, r * 0.5, -r * 0.15);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawGravestone(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  const tilt = Math.sin(o.x * 0.7 + o.y * 1.1) * 0.18;
  ctx.save();
  ctx.rotate(tilt);
  drawShadow(ctx, r * 1.05, 1, 0.3);

  ctx.beginPath();
  ctx.moveTo(-r * 0.7, 0);
  ctx.lineTo(-r * 0.7, -r * 1.1);
  ctx.arc(0, -r * 1.1, r * 0.7, Math.PI, 0);
  ctx.lineTo(r * 0.7, 0);
  ctx.closePath();
  ctx.fillStyle = '#8b93a1';
  ctx.fill();
  ctx.strokeStyle = '#575e69';
  ctx.lineWidth = 1.6;
  ctx.stroke();

  ctx.strokeStyle = 'rgba(60, 66, 76, 0.85)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.42);
  ctx.lineTo(0, -r * 0.72);
  ctx.moveTo(-r * 0.3, -r * 1.16);
  ctx.lineTo(r * 0.3, -r * 1.16);
  ctx.stroke();
  ctx.restore();
}

function drawCrypt(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.12, 1, 0.34);

  stone(ctx, -r, -r * 1.7, r * 2, r * 1.9, r * 0.12, '#79808c', '#4a5058');

  ctx.fillStyle = '#5b626d';
  ctx.beginPath();
  ctx.moveTo(-r * 1.15, -r * 1.7);
  ctx.lineTo(r * 1.15, -r * 1.7);
  ctx.lineTo(r * 0.95, -r * 2.35);
  ctx.lineTo(-r * 0.95, -r * 2.35);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#8f96a3';
  ctx.fillRect(-r * 0.82, -r * 1.6, r * 0.16, r * 1.6);
  ctx.fillRect(r * 0.66, -r * 1.6, r * 0.16, r * 1.6);

  ctx.fillStyle = '#20242b';
  ctx.beginPath();
  ctx.roundRect(-r * 0.3, -r * 1.15, r * 0.6, r * 1.15, [r * 0.26, r * 0.26, 0, 0]);
  ctx.fill();
  ctx.restore();
}

function drawChapel(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.14, 1, 0.34);

  // Nave.
  stone(ctx, -r * 1.25, -r * 1.5, r * 2.5, r * 1.7, r * 0.1, '#8a8577', '#544f43');
  ctx.fillStyle = '#7a4a3a';
  ctx.beginPath();
  ctx.moveTo(-r * 1.4, -r * 1.5);
  ctx.lineTo(r * 1.4, -r * 1.5);
  ctx.lineTo(0, -r * 2.5);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.14)';
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.5);
  ctx.lineTo(r * 1.4, -r * 1.5);
  ctx.lineTo(0, -r * 2.5);
  ctx.closePath();
  ctx.fill();

  // Door and round window.
  ctx.fillStyle = '#3a2b1e';
  ctx.beginPath();
  ctx.roundRect(-r * 0.24, -r * 0.95, r * 0.48, r * 1.15, [r * 0.22, r * 0.22, 0, 0]);
  ctx.fill();
  ctx.fillStyle = '#f2d38a';
  ctx.beginPath();
  ctx.arc(0, -r * 1.18, r * 0.16, 0, TAU);
  ctx.fill();

  // Steeple and cross.
  stone(ctx, -r * 0.3, -r * 3.5, r * 0.6, r * 1.5, 3, '#8a8577', '#544f43');
  ctx.fillStyle = '#6b4a3a';
  ctx.beginPath();
  ctx.moveTo(-r * 0.44, -r * 3.5);
  ctx.lineTo(r * 0.44, -r * 3.5);
  ctx.lineTo(0, -r * 4.6);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#e5c96a';
  ctx.lineWidth = Math.max(2, r * 0.09);
  ctx.beginPath();
  ctx.moveTo(0, -r * 4.72);
  ctx.lineTo(0, -r * 5.16);
  ctx.moveTo(-r * 0.2, -r * 5.0);
  ctx.lineTo(r * 0.2, -r * 5.0);
  ctx.stroke();
  ctx.restore();
}

function drawPillar(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  const h = r * 2.4;
  ctx.save();
  drawShadow(ctx, r * 1.05, 1, 0.32);

  stone(ctx, -r * 1.05, -r * 0.5, r * 2.1, r * 0.5, 3, '#8b93a1', '#575e69');
  stone(ctx, -r * 0.6, -h, r * 1.2, h - r * 0.5, 3, '#9aa1ad', '#616874');

  ctx.strokeStyle = 'rgba(80, 86, 96, 0.55)';
  ctx.lineWidth = 1.4;
  for (const fx of [-0.3, 0, 0.3]) {
    ctx.beginPath();
    ctx.moveTo(fx * r, -h + r * 0.1);
    ctx.lineTo(fx * r, -r * 0.55);
    ctx.stroke();
  }

  // Fractured top, as if the roof fell long ago.
  ctx.fillStyle = '#b0b7c2';
  ctx.beginPath();
  ctx.moveTo(-r * 0.6, -h);
  ctx.lineTo(r * 0.6, -h);
  ctx.lineTo(r * 0.35, -h - r * 0.28);
  ctx.lineTo(-r * 0.15, -h - r * 0.18);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawMonolith(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.15, 1, 0.34);

  ctx.beginPath();
  ctx.moveTo(-r * 0.7, 0);
  ctx.lineTo(-r * 0.55, -r * 2.1);
  ctx.lineTo(0, -r * 2.5);
  ctx.lineTo(r * 0.5, -r * 2.05);
  ctx.lineTo(r * 0.65, 0);
  ctx.closePath();
  ctx.fillStyle = '#6f7683';
  ctx.fill();
  ctx.strokeStyle = '#454b55';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Faint runes that pulse, hinting these stones are older than the city.
  const glow = 0.45 + 0.4 * Math.sin(o.x * 0.05 + o.y * 0.05);
  ctx.globalAlpha = glow;
  ctx.strokeStyle = '#9ad7ff';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.75);
  ctx.lineTo(0, -r * 1.0);
  ctx.moveTo(-r * 0.25, -r * 1.48);
  ctx.lineTo(r * 0.25, -r * 1.28);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawFence(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.1, 1, 0.24);

  ctx.strokeStyle = '#6b4f2a';
  ctx.lineCap = 'round';

  // Two posts...
  ctx.lineWidth = r * 0.3;
  for (const px of [-r * 0.78, r * 0.78]) {
    ctx.beginPath();
    ctx.moveTo(px, 0);
    ctx.lineTo(px, -r * 1.7);
    ctx.stroke();
  }

  // ...joined by a pair of rails, so each piece reads as fence, not a cross.
  ctx.lineWidth = r * 0.2;
  for (const ry of [-r * 1.42, -r * 0.92]) {
    ctx.beginPath();
    ctx.moveTo(-r * 0.92, ry);
    ctx.lineTo(r * 0.92, ry);
    ctx.stroke();
  }
  ctx.restore();
}

function drawWell(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.12, 1, 0.32);

  ctx.beginPath();
  ctx.ellipse(0, -r * 0.35, r, r * 0.6, 0, 0, TAU);
  ctx.fillStyle = '#7d8492';
  ctx.fill();
  ctx.strokeStyle = '#4a5058';
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(0, -r * 0.4, r * 0.72, r * 0.4, 0, 0, TAU);
  ctx.fillStyle = '#181c22';
  ctx.fill();

  ctx.strokeStyle = '#6b4f2a';
  ctx.lineWidth = r * 0.16;
  ctx.beginPath();
  ctx.moveTo(-r * 0.7, 0);
  ctx.lineTo(-r * 0.55, -r * 1.5);
  ctx.moveTo(r * 0.7, 0);
  ctx.lineTo(r * 0.55, -r * 1.5);
  ctx.stroke();

  ctx.fillStyle = '#7a4a3a';
  ctx.beginPath();
  ctx.moveTo(-r * 0.95, -r * 1.35);
  ctx.lineTo(r * 0.95, -r * 1.35);
  ctx.lineTo(0, -r * 2);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = '#c9b48a';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.35);
  ctx.lineTo(0, -r * 0.72);
  ctx.stroke();
  ctx.fillStyle = '#5b4426';
  ctx.fillRect(-r * 0.16, -r * 0.74, r * 0.32, r * 0.28);
  ctx.restore();
}

function drawScarecrow(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const r = o.r;
  ctx.save();
  drawShadow(ctx, r * 1.0, 1, 0.3);

  ctx.strokeStyle = '#6b4f2a';
  ctx.lineWidth = r * 0.22;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, -r * 2.4);
  ctx.moveTo(-r * 0.95, -r * 1.7);
  ctx.lineTo(r * 0.95, -r * 1.7);
  ctx.stroke();

  ctx.fillStyle = '#c9a227';
  ctx.beginPath();
  ctx.moveTo(-r * 0.7, -r * 1.9);
  ctx.lineTo(r * 0.7, -r * 1.9);
  ctx.lineTo(r * 0.5, -r * 0.9);
  ctx.lineTo(-r * 0.5, -r * 0.9);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, -r * 2.55, r * 0.5, 0, TAU);
  ctx.fillStyle = '#e0c17a';
  ctx.fill();
  ctx.strokeStyle = '#a98b46';
  ctx.lineWidth = 1.4;
  ctx.stroke();

  ctx.fillStyle = '#3a2b1e';
  ctx.beginPath();
  ctx.arc(-r * 0.18, -r * 2.62, r * 0.07, 0, TAU);
  ctx.arc(r * 0.18, -r * 2.62, r * 0.07, 0, TAU);
  ctx.fill();

  ctx.fillStyle = '#5b4426';
  ctx.beginPath();
  ctx.ellipse(0, -r * 2.85, r * 0.6, r * 0.14, 0, 0, TAU);
  ctx.fill();
  ctx.fillRect(-r * 0.34, -r * 3.25, r * 0.68, r * 0.42);
  ctx.restore();
}

/* ------------------------------------------------------------------------- guards */

/** A gate guard: armoured, spear and shield, slowly scanning the horizon. */
export function drawGuard(ctx: CanvasRenderingContext2D, t: number, radius = 21): void {
  const breathe = Math.sin(t * 1.8) * 0.5;
  const scan = Math.sin(t * 0.45) * 0.5;

  ctx.save();
  drawShadow(ctx, radius * 1.15, 1, 0.34);

  const bodyY = -radius * 1.15 + breathe;
  const bodyR = radius * 0.95;

  // Legs
  ctx.fillStyle = '#2f3a4d';
  ctx.beginPath();
  ctx.roundRect(-radius * 0.54, -radius * 0.52, radius * 0.38, radius * 0.58, 3);
  ctx.roundRect(radius * 0.16, -radius * 0.52, radius * 0.38, radius * 0.58, 3);
  ctx.fill();

  // Tabard
  ctx.fillStyle = '#3d5478';
  ctx.beginPath();
  ctx.roundRect(-bodyR * 0.86, bodyY - bodyR * 0.8, bodyR * 1.72, bodyR * 1.9, 4);
  ctx.fill();

  // Breastplate and pauldrons
  ctx.fillStyle = '#8794a8';
  ctx.beginPath();
  ctx.roundRect(-bodyR * 0.72, bodyY - bodyR * 0.72, bodyR * 1.44, bodyR * 1.05, 5);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-bodyR * 0.88, bodyY - bodyR * 0.5, bodyR * 0.42, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(bodyR * 0.88, bodyY - bodyR * 0.5, bodyR * 0.42, 0, TAU);
  ctx.fill();

  // Helmet
  const headY = bodyY - bodyR * 1.15;
  ctx.fillStyle = '#9aa7bb';
  ctx.beginPath();
  ctx.arc(0, headY, radius * 0.5, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#39424f';
  ctx.beginPath();
  ctx.roundRect(-radius * 0.42, headY - radius * 0.1, radius * 0.84, radius * 0.22, 2);
  ctx.fill();
  // Plume
  ctx.fillStyle = '#c2453f';
  ctx.beginPath();
  ctx.moveTo(0, headY - radius * 0.46);
  ctx.quadraticCurveTo(radius * 0.5, headY - radius * 0.95, -radius * 0.1, headY - radius * 1.15);
  ctx.quadraticCurveTo(radius * 0.2, headY - radius * 0.7, -radius * 0.24, headY - radius * 0.4);
  ctx.closePath();
  ctx.fill();

  // Spear held upright on the right, shield on the left
  ctx.save();
  ctx.translate(bodyR * 1.15, bodyY * 0.6);
  ctx.rotate(0.09 + scan * 0.05);
  ctx.fillStyle = '#7a5a38';
  ctx.fillRect(-radius * 0.05, -radius * 2.2, radius * 0.1, radius * 2.9);
  ctx.fillStyle = '#cfd6e2';
  ctx.beginPath();
  ctx.moveTo(0, -radius * 2.85);
  ctx.lineTo(radius * 0.19, -radius * 2.15);
  ctx.lineTo(-radius * 0.19, -radius * 2.15);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.translate(-bodyR * 1.05, bodyY - bodyR * 0.1);
  ctx.rotate(-0.1 + scan * 0.06);
  ctx.fillStyle = '#6f7683';
  ctx.beginPath();
  ctx.ellipse(0, 0, radius * 0.6, radius * 0.86, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = '#4a505b';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = '#e5b95c';
  ctx.beginPath();
  ctx.arc(0, 0, radius * 0.2, 0, TAU);
  ctx.fill();
  ctx.restore();

  // A subtle lantern glow so guards read as friendly at a glance
  ctx.globalAlpha = 0.22 + Math.sin(t * 1.6) * 0.05;
  ctx.fillStyle = '#9fd0ff';
  ctx.beginPath();
  ctx.arc(0, headY - radius * 2.4, radius * 0.22, 0, TAU);
  ctx.fill();

  ctx.restore();
}

/* -------------------------------------------------------------------- quest giver */

/** The bounty officer: a clerk in a plumed hat with a ledger and a banner. */
export function drawQuestGiver(ctx: CanvasRenderingContext2D, t: number, radius = 20): void {
  const bob = Math.sin(t * 1.7) * 0.5;
  const pageTurn = Math.sin(t * 0.9) * 0.08;

  ctx.save();
  drawShadow(ctx, radius * 1.4, 1, 0.32);

  // --- reading stand in front
  ctx.fillStyle = '#4a3324';
  ctx.beginPath();
  ctx.roundRect(-radius * 1.25, -radius * 1.0, radius * 2.5, radius * 1.05, 4);
  ctx.fill();
  ctx.fillStyle = '#6d4c34';
  ctx.beginPath();
  ctx.roundRect(-radius * 1.25, -radius * 1.12, radius * 2.5, radius * 0.4, 4);
  ctx.fill();

  // Pinned bounty notices on the board
  for (let i = -1; i <= 1; i++) {
    ctx.save();
    ctx.translate(i * radius * 0.62, -radius * 1.05);
    ctx.rotate(i * 0.07 + pageTurn * (i === 0 ? 1 : 0.4));
    ctx.fillStyle = '#e9dcb4';
    ctx.fillRect(-radius * 0.22, -radius * 0.5, radius * 0.44, radius * 0.52);
    ctx.fillStyle = '#b03a3a';
    ctx.fillRect(-radius * 0.22, -radius * 0.5, radius * 0.44, radius * 0.08);
    ctx.restore();
  }

  ctx.save();
  ctx.translate(0, bob);

  // --- coat
  ctx.fillStyle = '#3f6b4f';
  ctx.beginPath();
  ctx.moveTo(-radius * 0.95, -radius * 0.6);
  ctx.lineTo(radius * 0.95, -radius * 0.6);
  ctx.lineTo(radius * 0.66, -radius * 1.85);
  ctx.lineTo(-radius * 0.66, -radius * 1.85);
  ctx.closePath();
  ctx.fill();
  // Gold trim
  ctx.fillStyle = '#d8b25c';
  ctx.fillRect(-radius * 0.1, -radius * 1.85, radius * 0.2, radius * 1.25);
  ctx.fillRect(-radius * 0.9, -radius * 0.78, radius * 1.8, radius * 0.16);

  // --- shoulders
  ctx.fillStyle = '#4f8060';
  ctx.beginPath();
  ctx.arc(-radius * 0.82, -radius * 1.62, radius * 0.34, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(radius * 0.82, -radius * 1.62, radius * 0.34, 0, TAU);
  ctx.fill();

  // --- head
  const headY = -radius * 2.05;
  ctx.fillStyle = '#e8c6a0';
  ctx.beginPath();
  ctx.arc(0, headY, radius * 0.44, 0, TAU);
  ctx.fill();
  // Eyes
  ctx.fillStyle = '#243024';
  ctx.beginPath();
  ctx.arc(-radius * 0.16, headY + radius * 0.03, radius * 0.06, 0, TAU);
  ctx.arc(radius * 0.16, headY + radius * 0.03, radius * 0.06, 0, TAU);
  ctx.fill();

  // --- plumed hat
  ctx.fillStyle = '#2f523c';
  ctx.beginPath();
  ctx.ellipse(0, headY - radius * 0.28, radius * 0.88, radius * 0.17, 0, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-radius * 0.44, headY - radius * 0.3);
  ctx.quadraticCurveTo(-radius * 0.2, -radius * 3.1, radius * 0.4, headY - radius * 0.34);
  ctx.closePath();
  ctx.fill();
  // Feather
  ctx.fillStyle = '#e5b95c';
  const sway = Math.sin(t * 1.3) * 0.06;
  ctx.save();
  ctx.translate(radius * 0.42, headY - radius * 0.4);
  ctx.rotate(-0.5 + sway);
  ctx.beginPath();
  ctx.ellipse(0, -radius * 0.8, radius * 0.16, radius * 0.8, 0, 0, TAU);
  ctx.fill();
  ctx.restore();

  // --- ledger held in one hand
  ctx.save();
  ctx.translate(-radius * 1.15, -radius * 1.35);
  ctx.rotate(-0.22 + pageTurn * 0.5);
  ctx.fillStyle = '#7a4a3a';
  ctx.beginPath();
  ctx.roundRect(-radius * 0.5, -radius * 0.36, radius, radius * 0.72, 3);
  ctx.fill();
  ctx.fillStyle = '#efe6cd';
  ctx.fillRect(-radius * 0.42, -radius * 0.3, radius * 0.84, radius * 0.6);
  ctx.strokeStyle = 'rgba(110, 92, 60, 0.6)';
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(-radius * 0.32, -radius * 0.16 + i * radius * 0.16);
    ctx.lineTo(radius * 0.32, -radius * 0.16 + i * radius * 0.16);
    ctx.stroke();
  }
  ctx.restore();

  // --- banner pole
  ctx.save();
  ctx.translate(radius * 1.35, 0);
  ctx.fillStyle = '#5b4426';
  ctx.fillRect(-radius * 0.06, -radius * 3.3, radius * 0.12, radius * 3.3);
  const wave = Math.sin(t * 2.1) * radius * 0.08;
  ctx.fillStyle = '#b03a3a';
  ctx.beginPath();
  ctx.moveTo(radius * 0.06, -radius * 3.25);
  ctx.quadraticCurveTo(radius * 0.8 + wave, -radius * 3.05, radius * 1.1, -radius * 2.72);
  ctx.lineTo(radius * 0.06, -radius * 2.3);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#e5b95c';
  ctx.beginPath();
  ctx.arc(radius * 0.5, -radius * 2.78, radius * 0.14, 0, TAU);
  ctx.fill();
  ctx.restore();

  ctx.restore();
  ctx.restore();
}

/* ------------------------------------------------------------------- monument */

/**
 * The leaderboard statue in the plaza.
 *
 * The names are drawn in **world units**, not screen units like the other
 * nameplates, so they scale with the camera and genuinely feel carved into the
 * stone - walk up close and read them.
 */
export function drawMonument(
  ctx: CanvasRenderingContext2D,
  t: number,
  radius: number,
  entries: Array<{ rank: number; name: string; level: number; cls: ClassId; online: boolean }>,
): void {
  const r = radius;
  const shaftTop = -r * 8.2;
  const halfWidth = r * 1.75;

  ctx.save();
  drawShadow(ctx, r * 2.1, 1, 0.36);

  // --- stepped base
  ctx.fillStyle = '#4d545f';
  ctx.beginPath();
  ctx.roundRect(-r * 2.05, -r * 0.5, r * 4.1, r * 0.58, 6);
  ctx.fill();
  ctx.fillStyle = '#5d646f';
  ctx.beginPath();
  ctx.roundRect(-r * 1.75, -r * 0.95, r * 3.5, r * 0.5, 6);
  ctx.fill();
  ctx.fillStyle = '#6d7480';
  ctx.beginPath();
  ctx.roundRect(-r * 1.45, -r * 1.4, r * 2.9, r * 0.5, 6);
  ctx.fill();

  // --- shaft
  // NOTE: roundRect takes (x, y, width, height) with y as the TOP edge and a
  // positive height growing downward. Getting the sign wrong here made the
  // whole shaft draw below the base instead of rising from it.
  const shaftHeight = -r * 1.35 - shaftTop;
  ctx.beginPath();
  ctx.roundRect(-halfWidth, shaftTop, halfWidth * 2, shaftHeight, 6);
  ctx.fillStyle = '#7b828e';
  ctx.fill();
  ctx.strokeStyle = '#4b515b';
  ctx.lineWidth = 2.5;
  ctx.stroke();
  ctx.beginPath();
  ctx.roundRect(halfWidth * 0.25, shaftTop, halfWidth * 0.75, shaftHeight, 6);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.14)';
  ctx.fill();

  // --- recessed plaque
  const padX = r * 0.28;
  const plaqueLeft = -halfWidth + padX;
  const plaqueWidth = (halfWidth - padX) * 2;
  const plaqueTop = -r * 7.6;
  const plaqueHeight = r * 5.9;
  ctx.beginPath();
  ctx.roundRect(plaqueLeft, plaqueTop, plaqueWidth, plaqueHeight, 4);
  ctx.fillStyle = '#5f6672';
  ctx.fill();
  ctx.strokeStyle = '#444a53';
  ctx.lineWidth = 2;
  ctx.stroke();

  const pulse = 0.55 + Math.sin(t * 1.5) * 0.25;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Title
  ctx.font = `700 ${r * 0.34}px 'Segoe UI', system-ui, sans-serif`;
  ctx.fillStyle = `rgba(255, 226, 160, ${0.7 + pulse * 0.3})`;
  ctx.fillText('LEGENDS', 0, plaqueTop + r * 0.52);
  ctx.font = `600 ${r * 0.2}px 'Segoe UI', system-ui, sans-serif`;
  ctx.fillStyle = 'rgba(200, 210, 224, 0.6)';
  ctx.fillText('of the realm', 0, plaqueTop + r * 0.9);

  // Names
  const rowHeight = r * 0.72;
  let y = plaqueTop + r * 1.5;

  if (entries.length === 0) {
    ctx.font = `600 ${r * 0.3}px 'Segoe UI', system-ui, sans-serif`;
    ctx.fillStyle = 'rgba(180, 190, 205, 0.5)';
    ctx.fillText('No names yet', 0, y + r * 0.5);
  } else {
    for (const entry of entries.slice(0, 5)) {
      const accent = CLASS_PALETTE[entry.cls]?.light ?? '#e5b95c';

      ctx.font = `700 ${r * 0.3}px 'Segoe UI', system-ui, sans-serif`;
      ctx.fillStyle = 'rgba(229, 185, 92, 0.85)';
      ctx.textAlign = 'right';
      ctx.fillText(`${entry.rank}`, -plaqueWidth * 0.34, y);

      ctx.textAlign = 'left';
      ctx.font = `700 ${r * 0.32}px 'Segoe UI', system-ui, sans-serif`;
      ctx.fillStyle = entry.online ? accent : 'rgba(190, 200, 215, 0.72)';
      const label = entry.name.length > 11 ? `${entry.name.slice(0, 11)}.` : entry.name;
      ctx.fillText(label, -plaqueWidth * 0.26, y);

      ctx.textAlign = 'right';
      ctx.font = `700 ${r * 0.3}px 'Segoe UI', system-ui, sans-serif`;
      ctx.fillStyle = 'rgba(255, 233, 168, 0.9)';
      ctx.fillText(`${entry.level}`, plaqueWidth * 0.4, y);

      y += rowHeight;
    }
  }
  ctx.textAlign = 'center';

  // --- gold cap and glow
  ctx.fillStyle = '#c9a227';
  ctx.beginPath();
  ctx.moveTo(-halfWidth * 0.9, shaftTop + r * 0.1);
  ctx.lineTo(halfWidth * 0.9, shaftTop + r * 0.1);
  ctx.lineTo(0, shaftTop - r * 0.85);
  ctx.closePath();
  ctx.fill();

  const glow = ctx.createRadialGradient(0, shaftTop - r * 1.0, 0, 0, shaftTop - r * 1.0, r * 1.8);
  glow.addColorStop(0, `rgba(255, 232, 170, ${0.5 + pulse * 0.35})`);
  glow.addColorStop(1, 'rgba(255, 220, 140, 0)');
  ctx.fillStyle = glow;
  circle(ctx, 0, shaftTop - r * 1.0, r * 1.8);
  ctx.fill();

  ctx.fillStyle = '#ffeaa8';
  circle(ctx, 0, shaftTop - r * 1.0, r * 0.22);
  ctx.fill();

  ctx.restore();
}

/* ------------------------------------------------------------------------ widgets */
export function drawHealthBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  ratio: number,
  options: { hostile?: boolean; height?: number } = {},
): void {
  const height = options.height ?? 5;
  const clamped = Math.max(0, Math.min(1, ratio));
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.62)';
  ctx.beginPath();
  ctx.roundRect(x - width / 2 - 1, y - 1, width + 2, height + 2, 2.5);
  ctx.fill();

  const hue = clamped * 118;
  ctx.fillStyle = options.hostile
    ? `hsl(${8 + clamped * 20}, 72%, ${34 + clamped * 22}%)`
    : `hsl(${hue}, 62%, 46%)`;
  ctx.beginPath();
  ctx.roundRect(x - width / 2, y, Math.max(1, width * clamped), height, 2);
  ctx.fill();
  ctx.restore();
}
